import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { RootState } from "@Reducers/index";

/*
 * The Header's outer box, which decides how far it pushes the page down. The Header is a lazy chunk, so Router draws
 * HeaderPlaceholder (the same box, empty) while it loads: with an empty fallback the page below moves down by the
 * header's height when it arrives (classic /dashboard: 138 px, a layout shift of 0.10). Small and eager: no Header
 * code here.
 *
 * ⚠️ KNOWN BUG: below sm (other pages) and md (the dashboard) both boxes are `h-auto`, so the empty box has no height
 * there and on phones the page still moves down when the Header arrives.
 */

/** The portal dashboard's header box: tall while `expanded` (the `onReduceHeader` slice: the page is scrolled to the top). */
export const portalHeaderBoxClass = (expanded: boolean): string =>
  `flex ${expanded ? "items-start h-auto md:items-center md:h-48" : "items-center h-18"}`;

/** Every other page's header box. */
export const PROJECT_HEADER_BOX_CLASS =
  "flex flex-row justify-between items-center m-0 h-18.75 sticky top-0 bg-surface_neutral z-10 w-full max-sm:flex-wrap max-sm:h-auto";

/** The page the Header draws the portal dashboard header on. */
export const PORTAL_HEADER_PATH = "/dashboard";

/** The Header's box with nothing in it, for Router's Suspense fallback. */
export function HeaderPlaceholder() {
  const { pathname } = useLocation();
  const expanded = useSelector((state: RootState) => state.onReduceHeader);
  return (
    <div
      aria-hidden
      className={pathname === PORTAL_HEADER_PATH ? portalHeaderBoxClass(expanded) : PROJECT_HEADER_BOX_CLASS}
    />
  );
}
